"use client";

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton, Button, Typography, Tooltip, Menu, Badge, Box, Chip,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { callProc } from "../../../../services/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const TYPE_ICON = {
  INFO:    "notifications",
  SUCCESS: "check_circle",
  WARNING: "warning",
  ERROR:   "error",
};

const TYPE_COLOR = {
  INFO:    { bg: "#e0f2fe", color: "#0369a1" },
  SUCCESS: { bg: "#dcfce7", color: "#15803d" },
  WARNING: { bg: "#fef9c3", color: "#92400e" },
  ERROR:   { bg: "#fee2e2", color: "#b91c1c" },
};

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60)       return "just now";
  if (diff < 3600)     return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400)    return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 604800)   return `${Math.floor(diff / 86400)} day ago`;
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

// ─── Component ────────────────────────────────────────────────────────────────
const Notifications = () => {
  const navigate     = useNavigate();
  const queryClient  = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [clearing, setClearing] = useState(false);
  const open = Boolean(anchorEl);

  // ── Fetch with React Query — auto-caches, auto-polls every 60s ────────────
  const { data: notifications = [] } = useQuery({
    queryKey: ["admin-notifications"],
    queryFn:  () => callProc("NotificationList").then((r) => r.data?.data || []),
    refetchInterval: 60_000,
    staleTime:       30_000,
    retry: false,
  });

  const visible = notifications.slice(0, 5);

  const handleOpen  = (e) => { setAnchorEl(e.currentTarget); };
  const handleClose = ()  => { setAnchorEl(null); };

  const handleClearAll = async (e) => {
    e.stopPropagation();
    setClearing(true);
    try {
      await callProc("NotificationDismissAll");
      queryClient.setQueryData(["admin-notifications"], []);
    } catch {
      // silently ignore
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{ width: "35px", height: "35px", p: 0 }}
          aria-controls={open ? "notif-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          className="for-dark-notification"
        >
          <Badge
            badgeContent={visible.length || null}
            color="error"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 10,
                minWidth: 16,
                height: 16,
                padding: "0 4px",
              },
            }}
          >
            <NotificationsNoneIcon color="action" sx={{ fontSize: "24px" }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="notif-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            padding: 0,
            borderRadius: "10px",
            boxShadow: "0 4px 45px #0000001a",
            overflow: "visible",
            width: 360,
            mt: 1.5,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
            Notifications{" "}
            <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
              ({visible.length})
            </Box>
          </Typography>

          {visible.length > 0 && (
            <Button
              variant="text"
              size="small"
              onClick={handleClearAll}
              disabled={clearing}
              sx={{ textTransform: "none", fontSize: 12, fontWeight: 500, color: "#64748b", minWidth: "auto" }}
            >
              {clearing ? "Clearing…" : "Clear all"}
            </Button>
          )}
        </Box>

        {/* List */}
        <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
          {visible.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: "text.disabled" }}>
              <NotificationsNoneIcon sx={{ fontSize: 32, mb: 1, opacity: 0.4 }} />
              <Typography sx={{ fontSize: 13 }}>No notifications</Typography>
            </Box>
          ) : (
            visible.map((n) => {
              const typeStyle = TYPE_COLOR[n.type] ?? TYPE_COLOR.INFO;
              const icon      = TYPE_ICON[n.type]  ?? "notifications";
              return (
                <Box
                  key={n._id}
                  className="border-top"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: 2.5,
                    py: 1.5,
                    cursor: "default",
                    "&:hover": { backgroundColor: "#f8fafc" },
                    transition: "background-color 0.15s",
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      flexShrink: 0,
                      backgroundColor: typeStyle.bg,
                      color: typeStyle.color,
                      fontSize: 20,
                    }}
                    className="for-dark-secondary-bg"
                  >
                    <i className="material-symbols-outlined" style={{ fontSize: 20 }}>{icon}</i>
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#0f172a",
                        mb: 0.25,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {n.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#64748b",
                        mb: 0.5,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        lineHeight: 1.4,
                      }}
                    >
                      {n.message}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={n.target === "ALL" ? "All Users" : n.target}
                        size="small"
                        sx={{ height: 16, fontSize: 10, fontWeight: 700, px: 0.25 }}
                      />
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {timeAgo(n.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer */}
        <Box
          className="border-top"
          sx={{ textAlign: "center", py: 1.5, cursor: "pointer" }}
          onClick={() => { handleClose(); navigate("/website-management/notifications"); }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "primary.main" }}>
            See All Notifications
          </Typography>
        </Box>
      </Menu>
    </>
  );
};

export default Notifications;
